import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CSSProperties, useMemo } from "react";
import { Icon } from "@/components";
import { messageIllustrations } from "../../../assets/illustrations";
import { cx } from "../../utils/cx";
import { Message } from "../Message";
import type { TableAlign, TableColumn, TableProps } from "../Table/Table";
import tableStyles from "../Table/Table.module.scss";
import styles from "./SortableTable.module.scss";

export interface SortableTableProps<T>
    extends Omit<
        TableProps<T>,
        "selectable" | "selectedRowIds" | "onSelectedRowIdsChange"
    > {
    /** Called with the reordered rows after a successful drop. */
    onReorder: (rows: T[]) => void;
    /** Disables dragging (e.g. while saving or editing). */
    disabled?: boolean;
    /** Accessible label for the drag handle. Default: "Drag to reorder". */
    dragHandleLabel?: string;
}

const cellStyle = (
    width?: number | string,
    minWidth?: number | string,
    align?: TableAlign,
): CSSProperties => ({
    width,
    minWidth,
    textAlign: align,
});

type SortableRowProps<T> = {
    row: T;
    rowId: string;
    columns: TableColumn<T>[];
    disabled: boolean;
    dragHandleLabel: string;
    onRowClick?: (row: T) => void;
};

function SortableRow<T>({
    row,
    rowId,
    columns,
    disabled,
    dragHandleLabel,
    onRowClick,
}: SortableRowProps<T>) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: rowId, disabled });

    const style: CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition: transition ?? undefined,
    };

    return (
        <tr
            className={cx(tableStyles.row, isDragging && styles.rowDragging)}
            data-clickable={onRowClick ? true : undefined}
            data-dragging={isDragging || undefined}
            key={rowId}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            ref={setNodeRef}
            style={style}
        >
            <td
                className={cx(tableStyles.cell, styles.handleCell)}
                onClick={event => event.stopPropagation()}
            >
                <button
                    aria-label={dragHandleLabel}
                    className={cx(
                        styles.handle,
                        isDragging && styles.handleDragging,
                    )}
                    disabled={disabled}
                    type="button"
                    {...attributes}
                    {...listeners}
                >
                    <Icon icon="drag" />
                </button>
            </td>
            {columns.map(column => (
                <td
                    className={cx(tableStyles.cell, column.className)}
                    key={`${rowId}-${column.id}`}
                    style={cellStyle(
                        column.width,
                        column.minWidth,
                        column.align ?? "left",
                    )}
                >
                    {column.renderCell(row)}
                </td>
            ))}
        </tr>
    );
}

/**
 * Sortable variant of the DS Table — drag handle column + `@dnd-kit` reorder.
 * Reuses Table visual styles; does not support row selection.
 */
export function SortableTable<T>({
    rows,
    columns,
    getRowId,
    className,
    onReorder,
    onRowClick,
    disabled = false,
    dragHandleLabel = "Drag to reorder",
    scrollMaxHeight,
    stickyHeader = false,
    emptyMessage = "No data",
    emptyDescription,
    showEmptyGraphic = true,
}: SortableTableProps<T>) {
    const itemIds = useMemo(() => rows.map(getRowId), [rows, getRowId]);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 6 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        if (disabled) return;
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = itemIds.indexOf(String(active.id));
        const newIndex = itemIds.indexOf(String(over.id));
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

        onReorder(arrayMove(rows, oldIndex, newIndex));
    };

    const scrollBoxStyle =
        scrollMaxHeight != null
            ? {
                  maxHeight:
                      typeof scrollMaxHeight === "number"
                          ? `${scrollMaxHeight}px`
                          : scrollMaxHeight,
              }
            : undefined;

    const headCellClass = cx(
        tableStyles.headCell,
        stickyHeader && tableStyles.headCellSticky,
    );

    const emptyContent =
        typeof emptyMessage === "string" ? (
            <Message
                description={emptyDescription}
                graphic={
                    showEmptyGraphic ? (
                        <img
                            alt=""
                            className={tableStyles.emptyGraphic}
                            src={messageIllustrations.tablePanel}
                        />
                    ) : undefined
                }
                title={emptyMessage}
            />
        ) : (
            emptyMessage
        );

    const colSpan = columns.length + 1;

    return (
        <div
            className={cx(tableStyles.wrapper, styles.root, className)}
            data-ui="sortable-table"
        >
            <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                sensors={sensors}
            >
                <div
                    className={tableStyles.desktopScroll}
                    data-scroll-y={scrollMaxHeight != null ? true : undefined}
                    style={scrollBoxStyle}
                >
                    <table className={tableStyles.table}>
                        <thead>
                            <tr>
                                <th
                                    className={cx(
                                        headCellClass,
                                        styles.handleHeadCell,
                                    )}
                                    scope="col"
                                >
                                    <span className={styles.srOnly}>
                                        Reorder
                                    </span>
                                </th>
                                {columns.map(column => (
                                    <th
                                        className={cx(
                                            headCellClass,
                                            column.className,
                                        )}
                                        key={column.id}
                                        scope="col"
                                        style={cellStyle(
                                            column.width,
                                            column.minWidth,
                                            column.align ?? "left",
                                        )}
                                    >
                                        {column.header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td
                                        className={tableStyles.emptyCell}
                                        colSpan={colSpan}
                                    >
                                        {emptyContent}
                                    </td>
                                </tr>
                            ) : (
                                <SortableContext
                                    items={itemIds}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {rows.map(row => {
                                        const rowId = getRowId(row);
                                        return (
                                            <SortableRow
                                                columns={columns}
                                                disabled={disabled}
                                                dragHandleLabel={
                                                    dragHandleLabel
                                                }
                                                key={rowId}
                                                onRowClick={onRowClick}
                                                row={row}
                                                rowId={rowId}
                                            />
                                        );
                                    })}
                                </SortableContext>
                            )}
                        </tbody>
                    </table>
                </div>
            </DndContext>
        </div>
    );
}

export type { TableColumn, TableAlign };
export type SortableTableColumn<T> = TableColumn<T>;
